/**
 * El botón de la cámara que vive dentro de una barra de búsqueda.
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 *  POR QUÉ VA DENTRO DEL CAMPO Y NO AL LADO
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * Porque es la otra forma de escribir lo mismo. Buscar un producto se hace de
 * tres maneras —tecleando el nombre, pasando el lector láser, o apuntando la
 * cámara— y las tres acaban en la misma casilla. Un botón aparte, fuera del
 * campo, se lee como otra función distinta y hay que ir a buscarlo.
 *
 * Va pegado al borde derecho porque es donde el pulgar llega solo con el
 * teléfono en la mano, que es justo el aparato donde esto sirve.
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 *  EN LA PC NO ES LA CÁMARA: ES EL TELÉFONO (2026-11-02)
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * En la computadora la webcam apunta a la cara de quien está sentado, no al
 * producto: por eso aquí no salía nada. Desde que el teléfono se une a la PC
 * con un QR (ver `lib/telefonoDeLaPc.ts`), en la PC el botón es un teléfono, y
 * lleva un punto verde cuando ya hay uno conectado. Lo pidió el dueño: «en vez
 * del icono de cámara, un icono de teléfono».
 *
 * ⚠️ Los buscadores que corren la X para dejarle sitio al botón tienen que
 * preguntar lo mismo, con `hayBotonDeEscaner()`. Si no, el botón queda encima
 * del texto o la X flotando en un hueco.
 */

import { Camera, Smartphone } from "lucide-react";

import { cx } from "@/components/ui";
import { hayBotonDeEscaner, seEscaneaConLaCamara } from "@/lib/lecturaDeCodigos";
import { useTelefono } from "@/lib/telefonoDeLaPc";

export function BotonDeEscaner({
  onClick,
  className,
  titulo,
}: {
  onClick: () => void;
  /** Dónde se coloca dentro del campo. Cada barra tiene su alto. */
  className?: string;
  /** Solo para la cámara: en la PC el título dice lo del teléfono. */
  titulo?: string;
}) {
  const conTelefono = useTelefono((s) => s.conectado);
  if (!hayBotonDeEscaner()) return null;
  const camara = seEscaneaConLaCamara();
  const texto = camara
    ? (titulo ?? "Escanear con la cámara")
    : conTelefono
      ? "Escanear con el teléfono"
      : "Conectar el teléfono para escanear";

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={texto}
      title={texto}
      className={cx(
        /*
          `size-9` y no un icono suelto: en un teléfono hay que poder tocarlo sin
          apuntar. Un icono de 17 píxeles sin área alrededor se falla una de cada
          tres veces, y el fallo es tocar la casilla y que suba el teclado.
        */
        "absolute top-1/2 flex size-9 -translate-y-1/2 items-center justify-center rounded-[calc(var(--radius-control)-2px)]",
        "text-muted transition-colors hover:bg-surface-hover hover:text-accent active:scale-95",
        className,
      )}
    >
      {camara ? (
        <Camera size={18} />
      ) : (
        <span className="relative">
          <Smartphone size={18} />
          {conTelefono && (
            <span className="absolute -right-1 -top-1 size-2 rounded-full border border-surface bg-success" />
          )}
        </span>
      )}
    </button>
  );
}
